import { Controller } from "@hotwired/stimulus"

// TEMP SEAM — W6-owned lib, vendored locally until the W6 merge replaces the
// file (see lib/work/virtualize.js header; W7 spec T3 item 1).
import { virtualizeRows } from "../../../lib/work/virtualize"

// W8-owned pure libs (Track A item 4 + Track C): geometry, undo stack,
// announcements — shared helpers live in lib/work/, never inside the engine.
import {
  SCALE_REGISTRY,
  scaleMeta,
  isoToDays,
  daysToIso,
  pxToDays,
  snapDays,
  clampSpan,
  dayPct,
} from "../../../lib/work/timeline_geometry"
import { createUndoStack } from "../../../lib/work/undo"
import {
  resolveAnnouncement,
  ensureAnnouncer,
  announce,
} from "../../../lib/work/announce"

// ui--work-timeline — the work timeline's ONE engine (W8: the W7 render
// controller extended into the operable surface). Jobs:
//
//   (a) virtualization above the threshold over virtual UNITS (rowsHost
//       direct children: [data-work-group] blocks + [data-work-row] rows) —
//       the read-only W6 helper keeps its single-host invariant because the
//       group block IS the unit (swimlanes stay one-parent);
//   (b) tier-2 color resolution + theme flip (W7, unchanged);
//   (c) bar gestures ported from editable_grid_controller.js to the date
//       domain: 5px press→click promotion, move/resize modes, ghost
//       clone-into-offsetParent, rAF-batched transform-only ghost writes,
//       capture-once click suppression, zero-delta no-op finalize,
//       pointercancel mirror (the flow pattern — the calendar's TOUCH-2 gap
//       is explicitly NOT copied);
//   (d) commit contract: optimistic DOM+state apply BEFORE the bubbling
//       `ui--work:bar-commit {id, start_date, end_date, action,
//       gesture_token}` — the engine owns ZERO transport; the host glue
//       island translates to POST and calls public revertGesture(token) on
//       4xx through the shared mutation seam (consumed read-only THERE —
//       seam policy is never inlined into this file);
//   (e) undo/redo (one entry per gesture, cap 100, applyingSnapshot guard),
//       keyboard nudges + Esc-cancel (Track C), flow-F9 announcements;
//   (f) discrete scale switching (Track B, P7): setScale cancels an active
//       gesture, re-projects every bar as a pure geometry transform (no date
//       mutates), writes ?scale= replaceState-only (the engine is the
//       param's PERMANENT one-writer owner — the W8F convergence resolved
//       as the registry: docs/work/javascript-api.md, "URL-param ownership
//       registry"), emits `ui--work:scale-change {scale}`;
//   (g) swimlanes: accordion toggles emit `ui--work:group-collapse
//       {group_id, collapsed}`; the engine owns no collapse state.
//
// Events (W0 §2 reserved `ui--work:*` namespace), all bubbling CustomEvents
// from this.element with plain JSON-safe details: bar-activate (W7 seam),
// bar-commit, group-collapse, scale-change.
//
// Turbo-restoration safety (D1): connect() is idempotent; disconnect() is
// flag-guarded mid-gesture (flow :1362 parity) — no ghost survives the
// controller and no listener of a NEW gesture is stripped by an old teardown.
export default class extends Controller {
  static targets = ["viewport", "rowsHost"]

  static values = {
    scale: String,
    today: String,
    // JSON-safe defs; Ruby emits "" for nil → scanned to undefined (pin 6,
    // flow _scanOptionalNodeAttrs discipline).
    rows: String,
    bars: String,
    colorTokens: String,
    virtualizeOver: { type: Number, default: 200 },
    // W8 hydration: the SSR scale domain + the hand-mirrored registry row
    // (Ruby emits these ONLY when non-default — absent attr means default).
    domainStart: String,
    domainEnd: String,
    snapUnitDays: { type: Number, default: 1 },
    minSpanDays: { type: Number, default: 1 },
    // Host-overridable announcement copy (JSON map key → template).
    announcements: String,
  }

  // Port constants: editable_grid DRAG_THRESHOLD_PX (:36) and the 350ms
  // suppression window (:231-242); W1's FIFO revert cap (SNAPSHOT_CAP :112).
  // MILESTONE_PX mirrors bar_component.rb's diamond edge — the apply path
  // re-derives the SSR centering offset from it.
  static DRAG_THRESHOLD_PX = 5
  static CLICK_SUPPRESS_MS = 350
  static REVERT_CAP = 20
  static MILESTONE_PX = 12

  connect() {
    // Idempotent connect (D1): never double-attach after a Turbo restore.
    this._teardown()
    this._scale = this._scanOptional(this.scaleValue) || "month"
    this._today = this._scanOptional(this.todayValue)
    this._rowDefs = this._scanJson(this.rowsValue)
    this._barDefs = this._scanJson(this.barsValue)
    this._colorTokens = this._scanJson(this.colorTokensValue)
    this._announcements = this._scanJson(this.announcementsValue)
    this._domainFrom = isoToDays(this.domainStartValue) ?? 0
    this._domainTo = isoToDays(this.domainEndValue) ?? 0
    // The hand-mirrored registry echo (attrs exist only when non-default):
    // read raw (a Stimulus Number value cannot distinguish "absent" from its
    // default), nulled by setScale when the client-side scale diverges.
    this._snapUnitEcho = this._readPositiveNumberAttr("data-ui--work-timeline-snap-unit-days-value")
    this._minSpanEcho = this._readPositiveNumberAttr("data-ui--work-timeline-min-span-days-value")
    this._barState = new Map()
    this._undo = createUndoStack(100)
    this._revertable = new Map()
    this._gesture = null
    this._keyChain = null
    this._suppressCleanup = null
    this._raf = null
    this._hydrateBars()
    this._virtual = this._maybeVirtualize()
    this._applyTier2Colors()
    this._observeTheme()
    this._bindBarEvents()
    this._bindGestures()
    this._bindKeyboard()
    this._bindScaleSwitcher()
    this._bindAccordion()
    this._announcer = ensureAnnouncer(this.element)
  }

  disconnect() {
    this._teardown()
  }

  // -- Teardown (D1 + flow :1362 gesture guard) ----------------------------------

  _teardown() {
    // An in-flight gesture is cancelled FIRST: no ghost survives the
    // controller, and no listener of a NEW gesture is stripped by an old
    // teardown (silent — teardown announces nothing).
    this._cancelGesture(true)
    if (this._virtual) {
      this._virtual.destroy()
      this._virtual = null
    }
    if (this._themeObserver) {
      this._themeObserver.disconnect()
      this._themeObserver = null
    }
    if (this._onBarClick) {
      this.element.removeEventListener("click", this._onBarClick)
      this.element.removeEventListener("keydown", this._onBarKeydown)
      this._onBarClick = null
      this._onBarKeydown = null
    }
    if (this._onGestureDown) {
      this.element.removeEventListener("pointerdown", this._onGestureDown)
      this._onGestureDown = null
    }
    if (this._onKeyDown) {
      this.element.removeEventListener("keydown", this._onKeyDown)
      this._onKeyDown = null
    }
    if (this._onScaleChoice) {
      this.element.removeEventListener("click", this._onScaleChoice)
      this._onScaleChoice = null
    }
    if (this._onAccordion) {
      this.element.removeEventListener("accordion:open", this._onAccordion)
      this.element.removeEventListener("accordion:close", this._onAccordion)
      this._onAccordion = null
    }
    if (this._suppressCleanup) {
      this._suppressCleanup()
      this._suppressCleanup = null
    }
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    if (this._barState) this._barState.clear()
  }

  // -- (a) Virtualization over virtual UNITS (W7 (a) + W8 Track B) ---------------

  // The read-only W6 helper needs all rows in ONE parent. Swimlanes nest rows
  // inside group blocks, so the engine virtualizes UNITS: the rowsHost's
  // direct children that are rows OR group blocks (flat W7 DOM contains only
  // rows — same behavior as before; a group block is one unit and its nested
  // rows never re-parent individually).
  _maybeVirtualize() {
    const host = this.hasRowsHostTarget ? this.rowsHostTarget : null
    if (!host) return null
    const units = Array.from(
      host.querySelectorAll(":scope > [data-work-row], :scope > [data-work-group]"),
    )
    return virtualizeRows({
      viewport: this.hasViewportTarget ? this.viewportTarget : host,
      rows: units,
      threshold: this.virtualizeOverValue || 200,
    })
  }

  // -- Bar hydration (W8 Track A) -------------------------------------------------

  // The DOM is the geometry source of truth after SSR (and after any host
  // re-render): one state entry per [data-bar-id] from its ISO attrs. barsValue
  // (the def list) stays for labels/row context; geometry never reads it.
  _hydrateBars() {
    this.element.querySelectorAll("[data-bar-id]").forEach((el) => {
      const id = el.getAttribute("data-bar-id")
      const start = isoToDays(el.getAttribute("data-start-date"))
      const end = isoToDays(el.getAttribute("data-end-date"))
      if (!id || start === null || end === null) return
      this._barState.set(id, { id, start_days: start, end_days: end, el })
    })
  }

  _barEl(id) {
    const entry = this._barState.get(id)
    if (!entry || !entry.el || !entry.el.isConnected) return null
    return entry.el
  }

  // Editability is the single SSR flag (data-bar-editable="false" covers BOTH
  // the host permission flag and deliveries — BarComponent sets it for both).
  _barEditable(el) {
    return el.getAttribute("data-bar-editable") !== "false"
  }

  // Focusable-but-inert disabled bars (pin 38): announced, never hidden, and
  // never gesture-armed.
  _barDisabled(el) {
    return el.getAttribute("aria-disabled") === "true"
  }

  _barLabel(el) {
    const label = el.querySelector("[data-bar-label]")
    if (label && label.textContent) return label.textContent.trim()
    return el.getAttribute("aria-label") || el.getAttribute("data-bar-id") || ""
  }

  // Defensive gesture guard for collapsed groups (pin 28/30): a bar inside a
  // hidden collapsible body cannot be a pointer/focus target in practice,
  // but the engine also refuses geometry for any bar nested in a group block
  // whose body is display-hidden.
  _barGesturable(el) {
    if (!this._barEditable(el) || this._barDisabled(el)) return false
    const groupBody = el.closest("[data-work-group] [data-ui--accordion-target='content']")
    if (groupBody && groupBody.classList.contains("hidden")) return false
    return true
  }

  // -- (c) Gestures — the editable_grid grammar ported to the date domain -------

  // Root-delegated: NO per-bar listeners (pin 8) — one pointerdown listener on
  // the engine root resolves targets with closest(), exactly like
  // editable_grid :64-105.
  _bindGestures() {
    this._onGestureDown = (event) => this._onPointerDown(event)
    this.element.addEventListener("pointerdown", this._onGestureDown)
  }

  _onPointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return
    const grip = event.target.closest("[data-resize-grip]")
    const bar = event.target.closest("[data-bar-id]")
    if (!bar || !this.element.contains(bar)) return
    if (!this._barGesturable(bar)) return

    // Mode resolution (pin 8): grip inside the bar = edge-resize; bar body =
    // move. Grip match wins over a nested bar match by construction.
    const mode = grip ? (grip.getAttribute("data-resize-grip") === "start" ? "resize-start" : "resize-end") : "move"

    // The gesture's ONLY rects read: the plot container (the bar's
    // offsetParent) measured once here — the per-frame path never reads
    // layout again (Track D pin 40). dayWidth = plot px per day from the
    // hydrated domain; the frame loop is pure arithmetic + transform writes.
    const plot = bar.offsetParent || bar.parentElement
    const plotRect = plot ? plot.getBoundingClientRect() : null
    const domainDays = this._domainTo - this._domainFrom
    const dayWidthPx = plotRect && domainDays > 0 ? plotRect.width / domainDays : 0
    // P4: direction read ONCE per gesture, never per frame.
    const direction = plot ? getComputedStyle(plot).direction : "ltr"

    this._gesture = {
      bar,
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      promoted: false,
      dayWidthPx,
      rtl: direction === "rtl",
      entry: this._barState.get(bar.getAttribute("data-bar-id")),
    }
    this._armPointerListeners()
  }

  // Window-scoped listeners bound at gesture ARM (spec item 2 — pointercancel
  // binds mirroring pointerup); removed on every gesture end, never leaked.
  _armPointerListeners() {
    this._onWinMove = (event) => this._onPointerMove(event)
    this._onWinUp = (event) => this._onPointerUp(event)
    this._onWinCancel = (event) => this._onPointerCancel(event)
    window.addEventListener("pointermove", this._onWinMove)
    window.addEventListener("pointerup", this._onWinUp)
    window.addEventListener("pointercancel", this._onWinCancel)
  }

  _detachPointerListeners() {
    if (this._onWinMove) {
      window.removeEventListener("pointermove", this._onWinMove)
      window.removeEventListener("pointerup", this._onWinUp)
      window.removeEventListener("pointercancel", this._onWinCancel)
      this._onWinMove = null
      this._onWinUp = null
      this._onWinCancel = null
    }
  }

  _onPointerMove(event) {
    const gesture = this._gesture
    if (!gesture || event.pointerId !== gesture.pointerId) return
    const dx = event.clientX - gesture.startX
    const dy = event.clientY - gesture.startY

    // Press→click promotion (editable_grid :107-121): a press is a click
    // until the pointer crosses the 5px threshold — below it: no ghost, no
    // drag, native navigation proceeds untouched.
    if (!gesture.promoted) {
      if (Math.hypot(dx, dy) <= this.constructor.DRAG_THRESHOLD_PX) return
      gesture.promoted = true
      this._promoteGesture(gesture)
    }
    this._writeGhost(gesture, dx)
  }

  _promoteGesture(gesture) {
    this._ghost = this._createGhost(gesture.bar)
    gesture.baseW = this._ghost.getBoundingClientRect().width
    // A completed drag must never also trigger the bar's navigation click —
    // the capture-once suppression is armed at promotion (pin 12).
    this._armSuppression()
  }

  // rAF-batched transform-only ghost writes: zero reads, zero layout (Track
  // D). Physical px follow the POINTER; the inline/date math happens once at
  // finalize (P4 — direction was read at gesture start).
  _writeGhost(gesture, dx) {
    if (this._raf) return
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      const ghost = this._ghost
      if (!ghost) return
      const baseW = gesture.baseW || 0
      if (gesture.mode === "move") {
        ghost.style.transform = `translate3d(${dx}px, 0, 0)`
      } else if (gesture.mode === "resize-end") {
        ghost.style.transform = ""
        ghost.style.width = `${Math.max(2, gesture.rtl ? baseW - dx : baseW + dx)}px`
      } else {
        // resize-start: LTR moves the LEFT edge (translate + shrink); RTL
        // moves the RIGHT edge (grow/shrink in place).
        if (gesture.rtl) {
          ghost.style.transform = ""
          ghost.style.width = `${Math.max(2, baseW + dx)}px`
        } else {
          ghost.style.transform = `translate3d(${dx}px, 0, 0)`
          ghost.style.width = `${Math.max(2, baseW - dx)}px`
        }
      }
    })
  }

  // -- Gesture finalize / cancel ---------------------------------------------------

  _onPointerUp(event) {
    const gesture = this._gesture
    if (!gesture || event.pointerId !== gesture.pointerId) return
    this._detachPointerListeners()
    if (!gesture.promoted) {
      // Sub-threshold release: a plain click — nothing armed, nothing to
      // clean (the native click proceeds; bar-activate handles navigation).
      this._gesture = null
      return
    }
    const dx = event.clientX - gesture.startX
    this._gesture = null
    const result = this._finalizeGesture(gesture, dx)
    this._destroyGhost()
    if (!result) {
      // Zero-delta finalize (editable_grid :339-365): no commit, no undo
      // entry — the no-spam rule. Suppression stays armed for the trailing
      // click either way.
      return
    }
    this._announce(result.action === "move" ? "moved" : "resized", {
      label: this._barLabel(gesture.bar),
      start_date: daysToIso(result.entry.start_days),
      end_date: daysToIso(result.entry.end_days),
    })
  }

  // Compute the snapped, clamped dates for a promoted gesture. Returns the
  // applied entry + action, or null for a no-op (zero delta / no movement).
  _finalizeGesture(gesture, dx) {
    const entry = gesture.entry
    if (!entry) return null
    // Pointer travel re-expressed in the INLINE direction (P4), then px→days
    // (the editable_grid px↔minutes pair, date domain) and snapped to the
    // registry unit.
    const dxInline = gesture.rtl ? -dx : dx
    const delta = snapDays(pxToDays(dxInline, gesture.dayWidthPx), this._snapUnit())
    const min = this._minSpan()
    let next
    if (gesture.mode === "move") {
      if (delta === 0) return null
      next = { start: entry.start_days + delta, end: entry.end_days + delta }
    } else if (gesture.mode === "resize-start") {
      // A start-edge resize may never cross the anchor end; min span floors
      // the inclusive span (pin 13).
      const start = Math.min(entry.start_days + delta, entry.end_days - min + 1)
      next = clampSpan(start, entry.end_days, min)
    } else {
      const end = Math.max(entry.end_days + delta, entry.start_days + min - 1)
      next = clampSpan(entry.start_days, end, min)
    }
    const bounded = { start_days: next.start, end_days: next.end }
    if (bounded.start_days === entry.start_days && bounded.end_days === entry.end_days) return null
    const action = gesture.mode === "move" ? "move" : "resize"
    // A pointer finalize invalidates any nudge chain: its baseline predates
    // the drag, so an Esc after the drag must never restore across it.
    this._keyChain = null
    this._commitEntry(entry, bounded, action)
    return { entry, action }
  }

  // pointercancel mirrors pointerup (pin 10 — the TOUCH-2 gap NOT copied):
  // ghost torn down, listeners removed, no commit, no undo entry.
  _onPointerCancel(event) {
    const gesture = this._gesture
    if (!gesture || event.pointerId !== gesture.pointerId) return
    this._cancelGesture()
  }

  // Pointercancel-identical cleanup, shared by pointer cancel, Esc, setScale
  // and teardown (pins 10/22/11). `silent` suppresses the announcement (the
  // teardown path announces nothing).
  _cancelGesture(silent = false) {
    const wasPromoted = !!(this._gesture && this._gesture.promoted)
    this._detachPointerListeners()
    this._destroyGhost()
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    this._gesture = null
    // Suppression was armed at promotion — a cancelled gesture must not keep
    // swallowing the surface's genuine clicks for the next 350ms (W8F NIT).
    if (wasPromoted) this._disarmSuppression()
    if (wasPromoted && !silent) {
      this._announce("cancelled")
    }
  }

  // Ghost clone-into-offsetParent (editable_grid :246-267, ported verbatim):
  // anchoring to the bar's offsetParent is what makes the ghost land in the
  // right swimlane row. The clone strips its grips and is inert to pointers.
  _createGhost(bar) {
    // offsetParent is the swimlane row's plot container in every real
    // browser; parentElement is the same node (bars are its direct children)
    // and stands in where offsetParent is unimplemented (jsdom).
    const parent = bar.offsetParent || bar.parentElement
    if (!parent) return null
    const ghost = bar.cloneNode(true)
    ghost.setAttribute("data-work-timeline-ghost", "true")
    ghost.setAttribute("aria-hidden", "true")
    ghost.style.pointerEvents = "none"
    ghost.style.opacity = "0.85"
    ghost.style.zIndex = "30"
    ghost.querySelectorAll("[data-resize-grip]").forEach((grip) => grip.remove())
    parent.appendChild(ghost)
    return ghost
  }

  _destroyGhost() {
    if (this._ghost) {
      this._ghost.remove()
      this._ghost = null
    }
  }

  // _suppressNextClick port (:231-242): a CAPTURE-once listener swallows
  // exactly the next click (stopPropagation stops the engine's bubble-phase
  // bar-activate delegation before it sees it) and self-cleans within 350ms.
  _armSuppression() {
    this._disarmSuppression()
    const handler = (event) => {
      event.stopPropagation()
      event.preventDefault()
      cleanup()
    }
    const cleanup = () => {
      this.element.removeEventListener("click", handler, true)
      clearTimeout(timer)
      this._suppressCleanup = null
    }
    const timer = setTimeout(cleanup, this.constructor.CLICK_SUPPRESS_MS)
    this.element.addEventListener("click", handler, true)
    this._suppressCleanup = cleanup
  }

  _disarmSuppression() {
    if (this._suppressCleanup) {
      this._suppressCleanup()
      this._suppressCleanup = null
    }
  }

  // -- Commit contract + optimistic rollback (F4 seam consumed by the HOST) ------

  // The ONE converged mutation path (F4 × F8 interlock): finalize, undo,
  // redo and revertGesture ALL land here, so a revert restores geometry
  // through the exact same writes a commit made — byte-identical restore
  // (pin 17). Renders clamped; the state keeps raw values.
  //
  // Milestone-blind no more: a milestone (data-bar-kind="milestone",
  // bar_component) is a single-day diamond the SSR centers with
  // calc(<pct>% - MILESTONE_PX/2) — the apply path re-derives exactly that
  // centering and NEVER writes a width (a diamond has no span; a % width
  // would stretch its focus ring/hit area into a day-wide box).
  _applyBarState(entry) {
    const el = entry.el && entry.el.isConnected ? entry.el : null
    if (!el) return
    if (el.getAttribute("data-bar-kind") === "milestone") {
      const startPct = dayPct(entry.start_days, this._domainFrom, this._domainTo)
      el.style.insetInlineStart = `calc(${startPct}% - ${this.constructor.MILESTONE_PX / 2}px)`
    } else {
      const startPct = dayPct(entry.start_days, this._domainFrom, this._domainTo)
      const endPct = dayPct(entry.end_days + 1, this._domainFrom, this._domainTo)
      const left = Math.max(0, Math.min(100, startPct))
      const width = Math.max(0, Math.min(100 - left, endPct - startPct))
      el.style.insetInlineStart = `${left}%`
      el.style.width = `${width}%`
    }
    el.setAttribute("data-start-date", daysToIso(entry.start_days))
    el.setAttribute("data-end-date", daysToIso(entry.end_days))
  }

  // One gesture = one commit = one undo entry = one revertable token (pins
  // 14/19). Optimistic-first: state + DOM apply BEFORE the event dispatches
  // (DOM-first contract, Gap-fill 5 Layer 0).
  _commitEntry(entry, next, action) {
    const token = this._mintToken()
    this._undo.push({ id: entry.id, start_days: entry.start_days, end_days: entry.end_days })
    const applied = { ...entry, start_days: next.start_days, end_days: next.end_days }
    this._barState.set(entry.id, applied)
    this._applyBarState(applied)
    this._revertable.set(token, {
      entry: { id: entry.id, start_days: next.start_days, end_days: next.end_days },
      pre: { id: entry.id, start_days: entry.start_days, end_days: entry.end_days },
    })
    // FIFO cap — W1's SNAPSHOT_CAP parity (consumers that never revert
    // cannot grow the map unbounded).
    if (this._revertable.size > this.constructor.REVERT_CAP) {
      this._revertable.delete(this._revertable.keys().next().value)
    }
    this._emitCommit(applied, action, token)
    return token
  }

  _mintToken() {
    this._tokenCounter = (this._tokenCounter || 0) + 1
    if (globalThis.crypto?.randomUUID) {
      return `work-tl-${this._tokenCounter}-${globalThis.crypto.randomUUID()}`
    }
    return `work-tl-${this._tokenCounter}-${Math.random().toString(36).slice(2)}`
  }

  _emitCommit(entry, action, token) {
    // Plain JSON-safe detail — no DOM nodes, no index, no lane (full-def
    // intent rule). The gesture_token is the Layer 0 revert handle the host
    // passes back to revertGesture(token) on a 4xx.
    this.element.dispatchEvent(
      new CustomEvent("ui--work:bar-commit", {
        bubbles: true,
        detail: {
          id: entry.id,
          start_date: daysToIso(entry.start_days),
          end_date: daysToIso(entry.end_days),
          action,
          gesture_token: token,
        },
      }),
    )
  }

  // -- Public data API ------------------------------------------------------------

  getBar(id) {
    const entry = this._barState.get(id)
    if (!entry) return null
    return { id: entry.id, start_date: daysToIso(entry.start_days), end_date: daysToIso(entry.end_days) }
  }

  // Programmatic gestures (host islands + tests): same converged path as a
  // pointer finalize — optimistic apply, one commit, one undo entry.
  moveBar(id, startIso, endIso) {
    return this._programmaticGesture(id, startIso, endIso, "move")
  }

  resizeBar(id, startIso, endIso) {
    return this._programmaticGesture(id, startIso, endIso, "resize")
  }

  _programmaticGesture(id, startIso, endIso, action) {
    const entry = this._barState.get(id)
    const start = isoToDays(startIso)
    const end = isoToDays(endIso)
    if (!entry || start === null || end === null) return null
    // The SSR editable flag is authoritative on every write path — a
    // host-disabled bar refuses programmatic gestures too (pin 4 parity).
    if (entry.el && !this._barGesturable(entry.el)) return null
    // Same snap discipline as the pointer path (docs claim parity): bounds
    // snap to the scale's snap-unit grid, anchored at the domain start
    // (month snap 1 = plain day rounding, so date-granular hosts are
    // unaffected; quarter snap 7 lands bounds on whole weeks of the domain).
    const unit = this._snapUnit()
    const anchored = this._domainFrom
    const next = clampSpan(
      anchored + snapDays(start - anchored, unit),
      anchored + snapDays(end - anchored, unit),
      this._minSpan(),
    )
    if (next.start === entry.start_days && next.end === entry.end_days) return null
    // Same stale-baseline rule as a pointer finalize: the chain's baseline
    // predates the programmatic gesture.
    this._keyChain = null
    return this._commitEntry(entry, { start_days: next.start, end_days: next.end }, action)
  }

  // Layer 0 revert (the W1 revert(token) seam carried to the date domain):
  // restores the pre-gesture geometry through the converged path. One-shot —
  // the token is consumed on ANY outcome.
  revertGesture(token) {
    const rec = token && this._revertable.get(token)
    if (!rec) return false
    this._revertable.delete(token)
    // F4 Layer 1 stale-rollback guard (pin 18): a later gesture already moved
    // this bar — the stale revert is skipped, scope left for host refetch.
    const entry = this._barState.get(rec.entry.id)
    if (!entry) return false
    if (entry.start_days !== rec.entry.start_days || entry.end_days !== rec.entry.end_days) return false
    this._restoreSnapshot(rec.pre, null, null)
    return true
  }

  // -- Undo / redo (flow :2812-2870 parity) ------------------------------------------

  undo() {
    const pre = this._undo.undo()
    if (!pre) return false
    const entry = this._barState.get(pre.id)
    if (entry) {
      this._undo.pushRedo({ id: pre.id, start_days: entry.start_days, end_days: entry.end_days })
    }
    this._restoreSnapshot(pre, "undo", "undone")
    return true
  }

  redo() {
    const next = this._undo.redo()
    if (!next) return false
    const entry = this._barState.get(next.id)
    if (entry) {
      this._undo.push({ id: next.id, start_days: entry.start_days, end_days: entry.end_days })
    }
    this._restoreSnapshot(next, "redo", "redone")
    return true
  }

  // Restore through the converged path under the applyingSnapshot guard: the
  // restore pushes nothing (pin 20), and — the W8F audit ruling — it emits a
  // commit-SHAPED `ui--work:bar-commit` carrying the RESTORED dates and the
  // restore action ("undo" | "redo" | "cancel", no gesture_token — the
  // sansToken contract covers tokenless emits). The documented glue POSTs
  // every commit; a silent geometry revert would strand the server on the
  // moved dates forever. revertGesture stays eventless: the HOST called it,
  // so the host owns the compensating write.
  _restoreSnapshot(snapshot, action, announceKey) {
    this._undo.applying = true
    let restored
    try {
      const stored = this._barState.get(snapshot.id)
      restored = {
        id: snapshot.id,
        start_days: snapshot.start_days,
        end_days: snapshot.end_days,
        el: stored ? stored.el : null,
      }
      this._barState.set(snapshot.id, restored)
      this._applyBarState(restored)
    } finally {
      this._undo.applying = false
    }
    if (action) {
      this.element.dispatchEvent(
        new CustomEvent("ui--work:bar-commit", {
          bubbles: true,
          detail: {
            id: restored.id,
            start_date: daysToIso(restored.start_days),
            end_date: daysToIso(restored.end_days),
            action,
          },
        }),
      )
    }
    if (announceKey) this._announce(announceKey)
  }

  // Registry-driven gesture parameters — the registry (via the SSR scale) is
  // the authority; the hand-mirrored value attrs are its hydration echo.
  // Registry-driven gesture parameters. The SSR hand-mirror hydrates the
  // registry row (attrs emitted ONLY when non-default — the hydration echo),
  // so the echo is read when present and the registry is the fallback — the
  // mirrored values are no longer dead weight (W8F NIT). A client-side
  // setScale nulls the echoes: they describe the SSR scale, the registry
  // describes every scale the engine can switch to.
  _snapUnit() {
    return this._snapUnitEcho ?? scaleMeta(this._scale).snap_unit_days
  }

  _minSpan() {
    return this._minSpanEcho ?? scaleMeta(this._scale).min_span_days
  }

  _readPositiveNumberAttr(name) {
    const raw = this.element.getAttribute(name)
    if (raw === null) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }

  // -- Keyboard path (Track C) ------------------------------------------------------

  _bindKeyboard() {
    this._onKeyDown = (event) => this._onKeydown(event)
    this.element.addEventListener("keydown", this._onKeyDown)
  }

  // The flow form-field guard list: no key handling while focus is in a form
  // field or contenteditable (pin 37).
  _inFormField(target) {
    if (!(target instanceof Element)) return false
    if (target.isContentEditable) return true
    return !!target.closest("input, textarea, select, [contenteditable='true']")
  }

  _onKeydown(event) {
    if (this._inFormField(event.target)) return

    // Cmd/Ctrl+Z undo, +Shift redo — surface-scoped (pin 37).
    if ((event.metaKey || event.ctrlKey) && !event.altKey && (event.key === "z" || event.key === "Z")) {
      event.preventDefault()
      if (event.shiftKey) this.redo()
      else this.undo()
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return

    const bar = event.target.closest("[data-bar-id]")
    if (!bar || !this.element.contains(bar)) return

    if (event.key === "Escape") {
      // A chain that was ACTUALLY cancelled is consumed here: the accordion's
      // document-level Escape handler would otherwise collapse the group and
      // steal focus on top of the restore (the W8F audit's Esc conflict).
      // Esc with no chain to cancel still propagates — the accordion works.
      if (this._cancelNudgeChain()) event.stopPropagation()
      return
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return

    // Host-disabled bars are focusable-but-inert and ANNOUNCED — disabled,
    // never hidden (pin 38).
    if (!this._barGesturable(bar)) {
      event.preventDefault()
      this._announce("disabled", { label: this._barLabel(bar) })
      return
    }
    event.preventDefault()
    this._nudge(bar, event.key, event.shiftKey)
  }

  // One committed nudge = one gesture: one bar-commit, one undo entry, one
  // live-region announcement (pin 35). Consecutive nudges on the same bar
  // form a CHAIN whose baseline Esc restores (pin 37).
  _nudge(bar, key, shift) {
    const id = bar.getAttribute("data-bar-id")
    const entry = this._barState.get(id)
    if (!entry) return
    // Milestones move but never resize (bar_component: single-day diamonds
    // get no grips, so the pointer path cannot resize them) — the keyboard
    // path honors the same contract: a Shift+Arrow on a milestone would
    // commit a server write the apply branch renders as zero visual change.
    if (shift && bar.getAttribute("data-bar-kind") === "milestone") return
    if (!this._keyChain || this._keyChain.id !== id) {
      // Direction read ONCE per chain (P4): ArrowLeft/Right map to time by
      // the inline direction — in RTL the arrows mirror.
      const plot = bar.offsetParent || bar.parentElement
      const rtl = plot ? getComputedStyle(plot).direction === "rtl" : false
      this._keyChain = {
        id,
        rtl,
        baseline: { id, start_days: entry.start_days, end_days: entry.end_days },
        // The undo-stack depth the chain started at: Esc-cancel truncates the
        // stack back here, so a cancelled chain is never re-undoable.
        undo_depth: this._undo.undoDepth(),
      }
    }
    const unit = this._snapUnit()
    let delta = key === "ArrowLeft" ? -unit : unit
    if (this._keyChain.rtl) delta = -delta
    const min = this._minSpan()
    let next
    if (shift) {
      // Shift+Arrow resizes the matching edge (ArrowLeft → start, ArrowRight
      // → end); the edge never crosses its anchor, min span floors the span.
      if (key === "ArrowLeft") {
        next = clampSpan(Math.min(entry.start_days + delta, entry.end_days - min + 1), entry.end_days, min)
      } else {
        next = clampSpan(entry.start_days, Math.max(entry.end_days + delta, entry.start_days + min - 1), min)
      }
    } else {
      next = { start: entry.start_days + delta, end: entry.end_days + delta }
    }
    const bounded = { start_days: next.start, end_days: next.end }
    if (bounded.start_days === entry.start_days && bounded.end_days === entry.end_days) return
    const action = shift ? "resize" : "move"
    this._commitEntry(entry, bounded, action)
    this._announce(action === "move" ? "moved" : "resized", {
      label: this._barLabel(bar),
      start_date: daysToIso(bounded.start_days),
      end_date: daysToIso(bounded.end_days),
    })
  }

  // Esc cancels the in-flight nudge chain: geometry returns to the chain
  // baseline through the converged restore path — the restore emits the
  // commit-shaped event with action "cancel" (the host glue must learn the
  // move was taken back), and the chain's own undo entries are truncated so
  // the cancelled chain is not re-undoable (pin 37). Returns whether anything
  // was actually restored — the Escape branch stops propagation only then.
  _cancelNudgeChain() {
    const chain = this._keyChain
    this._keyChain = null
    if (!chain) return false
    const entry = this._barState.get(chain.id)
    if (!entry) return false
    // The chain's commits are taken back BEFORE the baseline check: even a
    // chain nudged out and back to its exact baseline must drop its undo
    // entries (and any redo entry derived from them) — they would resurrect
    // mid-chain state on the next Cmd+Z.
    this._undo.truncate(chain.undo_depth)
    this._undo.clearRedo()
    if (entry.start_days === chain.baseline.start_days && entry.end_days === chain.baseline.end_days) return false
    this._restoreSnapshot(chain.baseline, "cancel", "cancelled")
    return true
  }

  // -- Discrete scale switching (Track B, P7 — no free zoom) -------------------------

  // The engine's public scale switch (the dropdown radio group's selection
  // drives this). Cancels an active gesture FIRST (pointercancel-identical
  // cleanup, pin 22), then re-projects EVERY bar as a pure geometry
  // transform — percent geometry is scale-invariant over the fixed domain,
  // so the re-projection re-derives every bar's pct through the converged
  // path and mutates no date (pin 21); the px-per-day change lands in the
  // per-gesture plot measurement. ?scale= is written replaceState-only (F5
  // one-writer-per-param — the engine writer is the param's permanent owner;
  // the W8F convergence resolved as the URL-param ownership registry,
  // docs/work/javascript-api.md).
  setScale(scale) {
    if (!scale || !SCALE_REGISTRY[scale]) return false
    if (scale === this._scale) return false
    this._cancelGesture(true)
    this._cancelNudgeChain()
    this._scale = scale
    // The hydration echo described the SSR scale — the registry is the
    // authority for every scale the engine switches to.
    this._snapUnitEcho = null
    this._minSpanEcho = null
    this.scaleValue = scale
    this._barState.forEach((entry) => this._applyBarState(entry))
    this._syncScaleChrome()
    this._writeUrlScale(scale)
    this.element.dispatchEvent(
      new CustomEvent("ui--work:scale-change", { bubbles: true, detail: { scale } }),
    )
    return true
  }

  // Chrome honesty (the W8F audit's MAJOR 3): a client-side switch is only
  // honest if the control that offered it tells the truth afterward — the
  // trigger label re-reads the matching radio item's text, every
  // [data-scale-choice] item's aria-checked mirrors the new scale, and the
  // radio items' indicator dots follow ([data-dropdown-indicator] > span,
  // the same node the dropdown controller toggles on click). The
  // dropdown controller ALSO moves aria-checked + dots on click (idempotent
  // — same values); this sync covers programmatic setScale calls and the
  // pre-refetch window while the host glue re-renders from the event.
  _syncScaleChrome() {
    const items = Array.from(this.element.querySelectorAll("[data-scale-choice]"))
    if (items.length === 0) return
    items.forEach((item) => {
      const checked = item.getAttribute("data-scale-choice") === this._scale
      item.setAttribute("aria-checked", checked ? "true" : "false")
      item
        .querySelector("[data-dropdown-indicator]")
        ?.firstElementChild?.classList.toggle("hidden", !checked)
    })
    const active = items.find((item) => item.getAttribute("data-scale-choice") === this._scale)
    const label = this.element.querySelector("[data-scale-switcher-label]")
    if (active && label) label.textContent = (active.textContent || "").trim()
  }

  // The ?scale= param has exactly ONE writer (F5): this method — replaceState
  // only, never window.Turbo.visit, never pushState (pin 24). The registry
  // (docs/work/javascript-api.md, "URL-param ownership registry") names the
  // engine as the param's permanent owner; the param is client WRITE-ONLY —
  // nothing reads it back at boot, the host refetch on scale-change is the
  // replay path (pin 24b).
  _writeUrlScale(scale) {
    if (typeof window === "undefined" || !window.history) return
    try {
      const url = new URL(window.location.href)
      url.searchParams.set("scale", scale)
      window.history.replaceState({}, "", url)
    } catch {
      // An unparseable URL never breaks the scale switch.
    }
  }

  _bindScaleSwitcher() {
    this._onScaleChoice = (event) => {
      const choice = event.target.closest("[data-scale-choice]")
      if (!choice || !this.element.contains(choice)) return
      this.setScale(choice.getAttribute("data-scale-choice"))
    }
    this.element.addEventListener("click", this._onScaleChoice)
  }

  // -- Swimlanes: accordion bridge (Track B item 2) -----------------------------------

  // The collapsible's ui--accordion controller owns the toggle; the engine
  // only OBSERVES it and emits ui--work:group-collapse {group_id, collapsed}
  // per toggle (pin 31). No collapse state lives here.
  _bindAccordion() {
    this._onAccordion = (event) => {
      const group = event.target.closest("[data-work-group]")
      if (!group || !this.element.contains(group)) return
      this.element.dispatchEvent(
        new CustomEvent("ui--work:group-collapse", {
          bubbles: true,
          detail: {
            group_id: group.getAttribute("data-group-name") || undefined,
            collapsed: event.type === "accordion:close",
          },
        }),
      )
    }
    this.element.addEventListener("accordion:open", this._onAccordion)
    this.element.addEventListener("accordion:close", this._onAccordion)
  }

  // -- Announcements (flow F9 pattern) ---------------------------------------------------

  _announce(key, tokens = {}) {
    if (!this._announcer) this._announcer = ensureAnnouncer(this.element)
    announce(this._announcer, resolveAnnouncement(this._announcements, key), tokens)
  }

  // -- (b) Tier-2 color resolution ----------------------------------------------

  _applyTier2Colors() {
    const tokens = this._colorTokens
    if (!tokens) return // absent map → token-pure class map stands, no inline fill (pin 24)

    const styles = getComputedStyle(this._darkScope())
    // Bars and axis-level markers share the tier-2 path (spec T2 item 6):
    // any element carrying data-color-token gets its fill resolved.
    this.element.querySelectorAll("[data-color-token]").forEach((el) => {
      const token = el.getAttribute("data-color-token")
      const cssVar = token ? tokens[token] : null
      if (!cssVar) return
      const color = this._cssColor(cssVar, styles)
      if (color) el.style.backgroundColor = color
    })
  }

  // The surface's dark scope: the nearest wrapper carrying `.dark` (the
  // dark_mode preview's mechanism). Only when no wrapper exists does the
  // observer fall back to the documentElement.
  _darkScope() {
    return this.element.closest(".dark") || document.documentElement
  }

  // Port of chart_controller.cssColor's channel handling (chart_controller.js
  // :112-120): tokens store HSL channels, not colors — both space
  // ("205 23% 13%") and comma ("205, 23%, 13%") forms appear across the
  // light/dark token sets, channels carry decimals ("0 0% 96.1%"), and a
  // rejected channel string would render black.
  _cssColor(name, styles) {
    const raw = (styles.getPropertyValue(name) || "").trim()
    if (!raw) return ""
    const channels = raw.split(/\s*,\s*|\s+/).filter(Boolean)
    if (channels.length === 3 && channels.every((c) => /^\d+(?:\.\d+)?%?$/.test(c))) {
      return `hsl(${channels.join(" ")})`
    }
    return raw
  }

  // -- (c) Theme flip ------------------------------------------------------------

  _observeTheme() {
    if (this._themeObserver) return
    // Watch the whole document class-space, not the resolved scope: a
    // wrapper that starts LIGHT (the common production case) resolves the
    // scope to documentElement here, and a later `.dark` flip on that
    // wrapper would never be seen by an observer pinned to
    // documentElement's own class attribute. Tier-2 re-fills only when the
    // resolved dark-scope actually flips (guarded in _onThemeFlip), so
    // unrelated class mutations anywhere in the page are no-ops.
    this._themeObserver = new MutationObserver(() => this._onThemeFlip())
    this._themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    })
    this._darkActive = this._darkScope() !== document.documentElement
  }

  _onThemeFlip() {
    const dark = this._darkScope() !== document.documentElement
    if (dark === this._darkActive) return
    this._darkActive = dark
    this._applyTier2Colors()
  }

  // -- Selection seam (the wave's ONE event) --------------------------------------

  _bindBarEvents() {
    this._onBarClick = (event) => {
      // A grip is a resize-only target: a sub-threshold press on it (the
      // press-is-click grammar) must not navigate the bar (W8F NIT).
      if (event.target.closest("[data-resize-grip]")) return
      const bar = event.target.closest("[data-bar-id]")
      if (bar && this.element.contains(bar)) this._emitBarActivate(bar)
    }
    this._onBarKeydown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return
      // Form-field guard (keyboard.md's documented contract): a keydown
      // originating in a form control never activates a bar. The shared
      // guard's list also covers contenteditable, which the raw instanceof
      // chain it replaces missed.
      if (this._inFormField(event.target)) return
      const bar = event.target.closest("[data-bar-id]")
      if (!bar || !this.element.contains(bar)) return
      event.preventDefault() // exactly one event — no synthesized click
      this._emitBarActivate(bar)
    }
    this.element.addEventListener("click", this._onBarClick)
    this.element.addEventListener("keydown", this._onBarKeydown)
  }

  _emitBarActivate(bar) {
    // W8: host-disabled bars are focusable-but-inert — announced, never
    // activated (pin 38).
    if (this._barDisabled(bar)) {
      this._announce("disabled", { label: this._barLabel(bar) })
      return
    }
    const detail = {
      id: bar.getAttribute("data-bar-id"),
      row_id: bar.getAttribute("data-row-id") || undefined,
      url: bar.getAttribute("data-url") || undefined,
    }
    this.element.dispatchEvent(new CustomEvent("ui--work:bar-activate", { bubbles: true, detail }))
  }

  // -- Hydration (pin 6) -----------------------------------------------------------

  // "" → undefined; flow _scanOptionalNodeAttrs discipline for scalar attrs.
  _scanOptional(value) {
    return value ? value : undefined
  }

  // "" → undefined; malformed JSON → undefined (never throws mid-hydration).
  _scanJson(value) {
    if (!value) return undefined
    try {
      return JSON.parse(value)
    } catch {
      return undefined
    }
  }
}
